import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Crown, Sparkles, Mail, Share2, X, Check, Heart } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "becoming-her:finale-celebrated";

/**
 * Soft, on-brand celebration modal that opens the moment a listener
 * finishes Episode 06 (Season 1 Finale). Captures Season 2 waitlist
 * signups + offers a one-tap share — while the emotional high is landing.
 */
export const FinaleCelebration = () => {
    const { endedEpisode, clearEndedEpisode } = usePlayer();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [joined, setJoined] = useState(false);
    const [shareLabel, setShareLabel] = useState("Share my Becoming");

    // 24 confetti pieces — each gets randomized horizontal start + delay + tint
    const confetti = useMemo(
        () =>
            Array.from({ length: 24 }, (_, i) => ({
                id: i,
                left: Math.random() * 100,
                delay: Math.random() * 1.2,
                duration: 3.4 + Math.random() * 2.4,
                size: 6 + Math.random() * 6,
                tint: i % 3 === 0 ? "#D4AF37" : i % 3 === 1 ? "#B8902A" : "#E4D1EC",
                rotate: Math.random() * 360,
            })),
        [open]
    );

    // Listen for the audio "ended" event from PlayerContext
    useEffect(() => {
        if (!endedEpisode) return;
        if (endedEpisode.number !== 6) {
            clearEndedEpisode();
            return;
        }
        // Don't re-fire on replays for this device
        try {
            if (localStorage.getItem(SEEN_KEY) === "1") {
                clearEndedEpisode();
                return;
            }
        } catch (_) {
            /* ignore storage errors */
        }
        setOpen(true);
        try {
            localStorage.setItem(SEEN_KEY, "1");
        } catch (_) {
            /* ignore */
        }
        clearEndedEpisode();
    }, [endedEpisode, clearEndedEpisode]);

    // Manual preview trigger: ?finale=1 (handy for Coach Melissa to QA the moment).
    useEffect(() => {
        if (typeof window === "undefined") return;
        const params = new URLSearchParams(window.location.search);
        if (params.get("finale") === "1") setOpen(true);
    }, []);

    const close = () => {
        setOpen(false);
        setJoined(false);
        setEmail("");
    };

    const joinSeason2 = async (e) => {
        e?.preventDefault?.();
        if (!email || !email.includes("@")) {
            toast.error("Drop your email so we can save your seat.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/newsletter/subscribe`, { email });
            setJoined(true);
            toast.success("You're in. Season 2 is coming for you. 💜");
        } catch (_err) {
            toast.error("Something went sideways — try again in a moment.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleShare = async () => {
        const shareData = {
            title: "Becoming H.E.R. — Season 1 Finale",
            text:
                "I just finished Season 1 of Becoming H.E.R.: Breaking the Cycles. " +
                "Stop shrinking. Start rising. 💜",
            url: "https://becomingherpod.org",
        };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
                setShareLabel("Shared 💜");
            } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(
                    `${shareData.text}\n${shareData.url}`
                );
                setShareLabel("Copied to clipboard ✓");
            }
            setTimeout(() => setShareLabel("Share my Becoming"), 2500);
        } catch (_) {
            /* user cancelled — silent */
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="finale-celebration-overlay"
            className="fixed inset-0 z-[100] grid place-items-center px-4 animate-finale-fade"
            role="dialog"
            aria-modal="true"
            aria-labelledby="finale-celebration-title"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-lilac-900/55 backdrop-blur-sm"
                onClick={close}
                aria-hidden
            />

            {/* Confetti — purely decorative, behind the card */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
                {confetti.map((c) => (
                    <span
                        key={c.id}
                        className="finale-confetti"
                        style={{
                            left: `${c.left}%`,
                            width: `${c.size}px`,
                            height: `${c.size * 1.6}px`,
                            background: c.tint,
                            animationDelay: `${c.delay}s`,
                            animationDuration: `${c.duration}s`,
                            transform: `rotate(${c.rotate}deg)`,
                        }}
                    />
                ))}
            </div>

            {/* Card */}
            <article
                data-testid="finale-celebration-card"
                className="relative w-full max-w-lg rounded-[32px] overflow-hidden bg-gradient-to-br from-[#FFF7E5] via-white to-[#FAF5FC] border border-gold/40 shadow-[0_40px_120px_rgba(43,29,53,0.30)] ring-1 ring-gold/30 animate-finale-pop"
            >
                {/* Gold glow */}
                <div
                    className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl bg-gold/30"
                    aria-hidden
                />
                <div
                    className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full blur-3xl bg-lilac-300/40"
                    aria-hidden
                />

                <button
                    data-testid="finale-close-btn"
                    onClick={close}
                    aria-label="Close"
                    className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-full bg-white/80 hover:bg-white text-lilac-900 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="relative px-7 md:px-10 py-9 md:py-11">
                    {/* Crown badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold text-white text-[10px] font-bold tracking-[0.18em] uppercase shadow-[0_8px_22px_rgba(212,175,55,0.45)]">
                        <Crown className="w-3 h-3" /> Season 1 · Complete
                    </div>

                    <h2
                        id="finale-celebration-title"
                        className="mt-5 font-display text-3xl md:text-4xl text-lilac-900 leading-tight"
                    >
                        Look at you, <em className="italic gold-text">rising.</em>
                    </h2>

                    <p className="mt-4 text-lilac-700 leading-relaxed">
                        You just finished Season 1 of <em>Becoming H.E.R.</em> — every
                        cycle, every wound, every commissioning. That&rsquo;s not a small thing,
                        sis. That&rsquo;s a whole becoming. 💜
                    </p>

                    {/* Season 2 waitlist */}
                    {!joined ? (
                        <form
                            onSubmit={joinSeason2}
                            data-testid="finale-waitlist-form"
                            className="mt-7 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur p-5"
                        >
                            <div className="flex items-center gap-2 text-gold-deep eyebrow">
                                <Sparkles className="w-3.5 h-3.5" /> Season 2 · Waitlist
                            </div>
                            <p className="mt-2 text-sm text-lilac-900 font-medium">
                                Be first in line for the next rising.
                            </p>
                            <div className="mt-3 flex flex-col sm:flex-row gap-2">
                                <div className="relative flex-1">
                                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-lilac-700" />
                                    <input
                                        type="email"
                                        required
                                        autoFocus
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="your@email.com"
                                        data-testid="finale-waitlist-email"
                                        className="w-full pl-10 pr-4 h-11 rounded-full border border-lilac-200 bg-white text-sm text-lilac-900 placeholder-lilac-700/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="finale-waitlist-submit"
                                    className="h-11 px-6 rounded-full bg-lilac-900 hover:bg-lilac-700 disabled:opacity-60 text-white font-semibold text-sm whitespace-nowrap transition-colors"
                                >
                                    {submitting ? "Saving your seat…" : "Save my seat"}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div
                            data-testid="finale-waitlist-joined"
                            className="mt-7 rounded-2xl border border-gold bg-gradient-to-br from-[#FFF7E5] to-white p-5 flex items-start gap-3"
                        >
                            <span className="grid place-items-center w-9 h-9 rounded-full bg-gold text-white shrink-0">
                                <Check className="w-4 h-4" />
                            </span>
                            <div>
                                <div className="font-semibold text-lilac-900">
                                    You&rsquo;re on the Season 2 list.
                                </div>
                                <p className="text-sm text-lilac-700 leading-relaxed mt-1">
                                    First listen. First freebies. First seat at the next rising.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Share + close row */}
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleShare}
                            data-testid="finale-share-btn"
                            className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-gold hover:bg-gold-deep text-white font-semibold text-sm shadow-[0_12px_32px_rgba(212,175,55,0.35)] transition-all"
                        >
                            <Share2 className="w-4 h-4" />
                            {shareLabel}
                        </button>
                        <button
                            onClick={close}
                            data-testid="finale-keep-rising-btn"
                            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-lilac-200 hover:border-gold bg-white text-lilac-900 font-medium text-sm transition-colors"
                        >
                            <Heart className="w-4 h-4 text-gold-deep" /> Keep rising
                        </button>
                    </div>

                    <p className="mt-5 text-center text-[11px] italic text-lilac-700/80">
                        &ldquo;For I know the plans I have for you…&rdquo; — Jeremiah 29:11
                    </p>
                </div>
            </article>

            {/* Component-local styles for confetti + entrance */}
            <style>{`
                @keyframes finaleFadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes finalePop {
                    0%   { opacity: 0; transform: translateY(18px) scale(0.96); }
                    60%  { opacity: 1; transform: translateY(-2px) scale(1.01); }
                    100% { opacity: 1; transform: translateY(0)    scale(1); }
                }
                @keyframes confettiFall {
                    0%   { transform: translateY(-20vh) rotate(0deg); opacity: 0; }
                    10%  { opacity: 1; }
                    100% { transform: translateY(120vh) rotate(720deg); opacity: 0; }
                }
                .animate-finale-fade { animation: finaleFadeIn 360ms ease-out both; }
                .animate-finale-pop  { animation: finalePop 520ms cubic-bezier(.2,.9,.3,1.15) both; }
                .finale-confetti {
                    position: absolute;
                    top: -10vh;
                    border-radius: 2px;
                    animation-name: confettiFall;
                    animation-timing-function: cubic-bezier(.4,.1,.6,1);
                    animation-iteration-count: 1;
                    animation-fill-mode: both;
                    opacity: 0.92;
                }
                @media (prefers-reduced-motion: reduce) {
                    .animate-finale-fade,
                    .animate-finale-pop,
                    .finale-confetti { animation: none !important; }
                    .finale-confetti { display: none; }
                }
            `}</style>
        </div>
    );
};
